/**
 * Trial status utilities with safe defaults
 * Handles null/undefined values gracefully
 */

export type TrialStatusType = 'active' | 'expired' | 'inactive';

export interface ProfileTrialData {
  account_status: string | null;
  trial_start_date: string | null;
  trial_end_date: string | null;
  is_trial_active?: boolean | null;
}

/**
 * Get safe defaults for profile trial fields
 */
export function getSafeTrialDefaults(): {
  account_status: string;
  trial_start_date: null;
  trial_end_date: null;
  is_trial_active: boolean;
} {
  return {
    account_status: 'trial',
    trial_start_date: null,
    trial_end_date: null,
    is_trial_active: false,
  };
}

/**
 * Normalize profile data with safe defaults
 * Ensures all trial fields have valid values
 */
export function normalizeProfileTrialData(
  profile: Partial<ProfileTrialData> | null | undefined
): {
  account_status: string;
  trial_start_date: string | null;
  trial_end_date: string | null;
  is_trial_active: boolean;
} {
  const defaults = getSafeTrialDefaults();
  
  if (!profile) {
    return defaults;
  }

  return {
    account_status: profile.account_status || defaults.account_status,
    trial_start_date: profile.trial_start_date ?? defaults.trial_start_date,
    trial_end_date: profile.trial_end_date ?? defaults.trial_end_date,
    is_trial_active: profile.is_trial_active ?? defaults.is_trial_active,
  };
}

/**
 * Get trial status from profile data
 * Returns: 'active' | 'expired' | 'inactive'
 * Never throws, always returns a valid status
 */
export function getTrialStatus(profile: Partial<ProfileTrialData> | null | undefined): TrialStatusType {
  try {
    const normalized = normalizeProfileTrialData(profile);
    
    // If not in trial, return inactive
    if (normalized.account_status !== 'trial') {
      return 'inactive';
    }

    // If no end date, consider it active (edge case)
    if (!normalized.trial_end_date) {
      return 'active';
    }

    // Check if expired
    const trialEndDate = new Date(normalized.trial_end_date);
    const today = new Date();
    
    // Reset time to compare dates only
    today.setHours(0, 0, 0, 0);
    trialEndDate.setHours(0, 0, 0, 0);

    if (today > trialEndDate) {
      return 'expired';
    }

    return 'active';
  } catch (error) {
    // If anything goes wrong, default to inactive (safe fallback)
    console.error('[getTrialStatus] Error calculating trial status:', error);
    return 'inactive';
  }
}

/**
 * Check if trial is expired
 * Safe wrapper around getTrialStatus
 */
export function isTrialExpired(profile: Partial<ProfileTrialData> | null | undefined): boolean {
  return getTrialStatus(profile) === 'expired';
}

/**
 * Calculate days remaining in trial
 * Returns 0 if expired or no end date
 */
export function getTrialDaysRemaining(profile: Partial<ProfileTrialData> | null | undefined): number {
  try {
    const normalized = normalizeProfileTrialData(profile);
    
    if (!normalized.trial_end_date) {
      return 0;
    }

    const trialEndDate = new Date(normalized.trial_end_date);
    const today = new Date();
    
    const diffTime = trialEndDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(0, diffDays);
  } catch (error) {
    console.error('[getTrialDaysRemaining] Error calculating days remaining:', error);
    return 0;
  }
}

